import {Component} from 'react';
import * as Sentry from '@sentry/nextjs';
import styles from './styles.module.scss';
import {Button} from './common-components';
import {NextImg} from '@/utils/nextImg';
import {openLiveChat} from '@/utils/intercom';
import {getDomain} from '@/utils/url';
import {linkgraphDomains} from '@/utils/router';
import {trace} from '@opentelemetry/api';
import {TracerReportButton} from './common-components/components/tracer-report-button';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Tooltip} from 'antd';
import {faCopy} from '@fortawesome/pro-regular-svg-icons';

interface ErrorBoundaryState {
  hasError: boolean;
  isWhiteLabelDomain: boolean;
  isGHLAccount: boolean;
  traceId: string | null;
  submitted: boolean;
  reportButtonDisabled: boolean;
  skipErrorUI: boolean;
}

export class ErrorBoundary extends Component<{children: any}, ErrorBoundaryState> {
  private readonly BUTTON_HIDE_TIMEOUT_MS = 60000;

  constructor(props) {
    super(props);

    const currentDomain = getDomain(window.location.hostname);
    const isWhiteLabelDomain = !linkgraphDomains.includes(currentDomain);
    const isGHLAccount = localStorage.getItem('isGHLAccount') === 'true';

    this.state = {
      hasError: false,
      isWhiteLabelDomain,
      isGHLAccount,
      traceId: null,
      submitted: false,
      reportButtonDisabled: false,
      skipErrorUI: false,
    };
  }

  static getDerivedStateFromError() {
    return {hasError: true, skipErrorUI: false};
  }

  componentDidCatch(error, errorInfo) {
    // Check if this error was already handled by notification system
    if (error && error.isNotificationHandled) {
      // If so, skip showing our error UI but still set hasError
      this.setState({
        hasError: true,
        skipErrorUI: true,
      });
      return;
    }

    // Reporting the error to Sentry
    Sentry.captureException(error);

    // Generate a traceId for the error to track it
    let traceId = '';
    const tracer = trace.getTracer('unhandled_error');
    tracer.startActiveSpan('error_boundary_catch', span => {
      span.setAttribute('error', true);
      span.setAttribute('error.message', error.message || 'Unknown error');
      span.setAttribute('error.stack', error.stack || '');
      // Store component info if available
      if (errorInfo && errorInfo.componentStack) {
        span.setAttribute('error.component_stack', errorInfo.componentStack);
      }
      // Get the traceId from the span context
      traceId = span.spanContext().traceId;
      // End the span
      span.end();
    });

    // Update state with the traceId and error details
    this.setState({
      hasError: true,
      traceId,
    });
  }

  hideButton = () => {
    this.setState({reportButtonDisabled: true, submitted: true});

    // Show the button again
    setTimeout(() => {
      this.setState({reportButtonDisabled: false});
    }, this.BUTTON_HIDE_TIMEOUT_MS);
  };

  render() {
    // If error was already handled by notification system, just render children
    if (this.state.skipErrorUI) {
      return this.props.children; // Return children instead of null
    }

    if (this.state.hasError) {
      return (
        <div className={styles.errorPage}>
          <div className={styles.errorPageContainer}>
            <NextImg src='/img/error-bird.svg' alt='bird-icon' width={100} height={100} />
            <h1>Uh Oh!</h1>
            <p>Something went wrong at our end.
              We&apos;ll be up and running soon.</p>
            {this.state.traceId && <span style={{color: '#FFFFFF', whiteSpace: 'nowrap', marginBottom: '10px'}}>Trace ID: {this.state.traceId} <Tooltip title='Copy to clipboard'><FontAwesomeIcon style={{cursor: 'pointer'}} icon={faCopy} onClick={() => navigator.clipboard.writeText(this.state.traceId)}/></Tooltip></span>}
            <div className={styles.errorPageContainerActions}>
              {!this.state.isWhiteLabelDomain && !this.state.isGHLAccount && <Button buttonType='primary' style={{marginRight: '10px'}}
                onClick={()=>{
                  openLiveChat(this.state.traceId);
                }}
              >
                Talk to our team
              </Button>}
              {this.state.traceId && <TracerReportButton
                traceId={this.state.traceId}
                status={this.state.submitted ? 1 : 0}
                description="Something went wrong at our end. We'll be up and running soon."
                message="Something went wrong at our end. We'll be up and running soon."
                onClick={this.hideButton}
                disabled={this.state.reportButtonDisabled}
              />}
              <Button
                style={{marginLeft: '10px'}}
                onClick={()=> window.location.reload()}
                buttonType='super-light-gray'>
                Refresh page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
